import type { AssistantPanelModel } from '.';
import {
    addWidgetExternalTool,
    addWidgetInputSchema,
    addWidgetToolName,
    assistantExternalTools,
    createAddWidgetToolSuccessOutput,
    queryHelpCenterInputSchema,
    queryHelpCenterToolName,
} from '../externalTools';
import type { AssistantPanelResultDispatcher } from '../services/AssistantPanelResultDispatcher/interface';
import { AssistantChatStateModel } from './state/model';
import { Chat } from '@ai-sdk/react';
import type { AssistantChatRequestBody } from '@thinkalpha/platform-ws-client/contracts/assistant.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import type { ChatOnToolCallCallback, UIMessage } from 'ai';
import { helpDocuments } from 'src/features/help/content/documents';
import { helpGuides } from 'src/features/help/content/guides';
import { searchHelpCenter } from 'src/features/help/content/search';
import type { HelpSidebarModel } from 'src/features/help/HelpSidebarModel';
import { inject, injectable } from 'src/features/ioc';
import { appConfig } from 'src/lib/config/appConfig';
import { useRustAssistant } from 'src/lib/featureFlags/flags/useRustAssistant';
import { allWidgets } from 'src/lib/widget/widgets';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import type { FeatureFlagService } from 'src/services/FeatureFlagService';
import type { RootInstance as Store, RootState } from 'src/store';
import { getCurrentAccessToken } from 'src/store/selectors/auth';

type AssistantUiContext = {
    activeWidgetType?: WidgetType;
    openWidgetTypes?: WidgetType[];
};

type AssistantToolCall = {
    toolName: string;
    toolCallId: string;
    input: unknown;
};

type AssistantUIMessage = UIMessage & AssistantChatRequestBody['messages'][number];

const MAX_UI_CONTEXT_WIDGET_TYPES = 20;
const hasOwn = (target: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(target, key);
const formatValidationError = (error: { issues: { message: string }[] }) =>
    error.issues.map((issue) => issue.message).join('; ');

type ChatStateSubscriptions = {
    '~registerMessagesCallback': (onChange: () => void, throttleWaitMs?: number) => () => void;
    '~registerStatusCallback': (onChange: () => void) => () => void;
    '~registerErrorCallback': (onChange: () => void) => () => void;
};

function getChatStateSubscriptions(chat: Chat<AssistantUIMessage>, log: Logger): ChatStateSubscriptions {
    const candidate = chat as Partial<ChatStateSubscriptions>;
    if (
        typeof candidate['~registerMessagesCallback'] !== 'function' ||
        typeof candidate['~registerStatusCallback'] !== 'function' ||
        typeof candidate['~registerErrorCallback'] !== 'function'
    ) {
        const message = 'AI SDK Chat runtime does not expose state subscription callbacks';
        log.error(message);
        throw new Error(message);
    }

    return candidate as ChatStateSubscriptions;
}

export const getAssistantUiContext = (state: RootState): AssistantUiContext | undefined => {
    const activeContainerId =
        state.container.activeContainerId ?? state.container.mruOrder.find((id) => state.container.byId[id]);
    const activeContainer = activeContainerId ? state.container.byId[activeContainerId] : undefined;
    const activeWidgetType = activeContainer?.activeTabId
        ? state.tab.byId[activeContainer.activeTabId]?.widget.type
        : undefined;
    const openWidgetTypes = [
        ...new Set(Object.values(state.tab.byId).flatMap((tab) => (tab?.widget.type ? [tab.widget.type] : []))),
    ].slice(0, MAX_UI_CONTEXT_WIDGET_TYPES);
    const context: AssistantUiContext = {
        ...(activeWidgetType ? { activeWidgetType } : {}),
        ...(openWidgetTypes.length ? { openWidgetTypes } : {}),
    };

    return Object.keys(context).length > 0 ? context : undefined;
};

@injectable()
export class AssistantPanelModelImpl implements AssistantPanelModel {
    readonly #log: Logger;
    readonly #store: Store;
    readonly #dispatcher: AssistantPanelResultDispatcher;
    readonly #helpSidebarModel: HelpSidebarModel;
    readonly #currentUserModel: CurrentUserModel;
    readonly #featureFlagService: FeatureFlagService;
    readonly #state = new AssistantChatStateModel();
    readonly #chat: Chat<AssistantUIMessage>;

    constructor(
        @inject('Logger') logger: Logger,
        @inject('Store') store: Store,
        @inject('AssistantPanelResultDispatcher') dispatcher: AssistantPanelResultDispatcher,
        @inject('HelpSidebarModel') helpSidebarModel: HelpSidebarModel,
        @inject('CurrentUserModel') currentUserModel: CurrentUserModel,
        @inject('FeatureFlagService') featureFlagService: FeatureFlagService,
    ) {
        this.#log = logger.getSubLogger({ name: 'feat:assistant:AssistantPanelModel' });
        this.#store = store;
        this.#dispatcher = dispatcher;
        this.#helpSidebarModel = helpSidebarModel;
        this.#currentUserModel = currentUserModel;
        this.#featureFlagService = featureFlagService;
        this.#chat = new Chat<AssistantUIMessage>({
            transport: this.#createTransport(),
            onToolCall: this.#handleToolCall,
            sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
            onError: (err) => {
                this.#log.error('Assistant chat onError callback', {
                    error: err.message,
                    stack: err.stack,
                    name: err.name,
                    cause: err.cause,
                });
            },
            onFinish: ({ message, isAbort, isDisconnect, isError, finishReason }) => {
                this.#log.info('Assistant chat finished', {
                    isAbort,
                    isDisconnect,
                    isError,
                    finishReason,
                    partCount: message.parts.length,
                    partTypes: message.parts.map((p) => p.type),
                });
            },
        });
        this.#syncChatState();
        const chatSubscriptions = getChatStateSubscriptions(this.#chat, this.#log);
        chatSubscriptions['~registerMessagesCallback'](() => {
            this.#state.setMessages(this.#chat.messages);
        });
        chatSubscriptions['~registerStatusCallback'](() => {
            this.#state.setStatus(this.#chat.status);
        });
        chatSubscriptions['~registerErrorCallback'](() => {
            this.#state.setError(this.#chat.error);
            if (this.#chat.error) {
                this.#log.error('Assistant chat error', {
                    error: this.#chat.error.message,
                    stack: this.#chat.error.stack,
                    name: this.#chat.error.name,
                    cause: this.#chat.error.cause,
                });
            }
        });
    }

    get messages() {
        return this.#state.messages;
    }

    get status() {
        return this.#state.status;
    }

    get error() {
        return this.#state.error;
    }

    get errorMessage() {
        return this.#state.errorMessage;
    }

    get isLoading() {
        return this.#state.isLoading;
    }

    get renderableMessages() {
        return this.#state.renderableMessages;
    }

    get showLoadingPlaceholder() {
        return this.#state.showLoadingPlaceholder;
    }

    send(text: string): boolean {
        const trimmed = text.trim();
        if (!trimmed) return false;

        if (!this.#getAccessToken()) {
            this.#log.error('Assistant chat aborted: no access token available');
            return false;
        }

        this.#log.info('Sending assistant chat message', {
            textLength: trimmed.length,
            api: this.#getChatApi(),
        });
        void this.#chat.sendMessage({ text: trimmed });
        return true;
    }

    clear(): void {
        void this.#chat.stop();
        this.#chat.messages = [];
        this.#state.setMessages([]);
        this.#state.setStatus('ready');
        this.#chat.clearError();
        this.#state.setError(undefined);
    }

    stop(): Promise<void> {
        return this.#chat.stop();
    }

    clearError(): void {
        this.#chat.clearError();
        this.#state.setError(undefined);
    }

    #createTransport() {
        return new DefaultChatTransport<AssistantUIMessage>({
            api: this.#getChatApi(),
            headers: (): Record<string, string> => {
                const token = this.#getAccessToken();
                if (TRACE) {
                    this.#log.trace('Building assistant chat headers', {
                        api: this.#getChatApi(),
                        hasToken: Boolean(token),
                    });
                }
                return token ? { Authorization: `Bearer ${token}` } : {};
            },
            prepareSendMessagesRequest: ({ credentials, headers, messages }) => {
                const api = this.#getChatApi();
                const state = this.#store.getState();
                const uiContext = getAssistantUiContext(state);
                const externalTools = this.#isHelpCenterEnabled() ? assistantExternalTools : [addWidgetExternalTool];
                if (TRACE) {
                    this.#log.trace('Preparing assistant chat request', {
                        api,
                        hasUiContext: Boolean(uiContext),
                        activeWidgetType: uiContext?.activeWidgetType,
                        openWidgetTypeCount: uiContext?.openWidgetTypes?.length ?? 0,
                        externalToolCount: externalTools.length,
                    });
                }
                return {
                    api,
                    credentials,
                    headers,
                    body: {
                        messages,
                        externalTools,
                        ...(uiContext ? { uiContext } : {}),
                    } satisfies AssistantChatRequestBody,
                };
            },
        });
    }

    #getChatApi(): string {
        const baseUrl = this.#featureFlagService.getValue(useRustAssistant) ? appConfig.assistantApi : appConfig.api;
        return `${baseUrl.replace(/\/$/, '')}/assistant/chat`;
    }

    #getAccessToken(): string | undefined {
        return getCurrentAccessToken(this.#store.getState());
    }

    #isHelpCenterEnabled(): boolean {
        return this.#currentUserModel.helpCenterAccess.value.isEnabled;
    }

    #syncChatState(): void {
        this.#state.setMessages(this.#chat.messages);
        this.#state.setStatus(this.#chat.status);
        this.#state.setError(this.#chat.error);
    }

    readonly #handleToolCall: ChatOnToolCallCallback<UIMessage> = async ({ toolCall }) => {
        await this.#handleAssistantToolCall(toolCall);
    };

    async #handleAssistantToolCall(toolCall: AssistantToolCall): Promise<void> {
        if (toolCall.toolName === queryHelpCenterToolName) {
            if (!this.#isHelpCenterEnabled()) {
                this.#chat.addToolOutput({
                    tool: queryHelpCenterToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: false, error: 'Help center search is disabled.' },
                });
                return;
            }

            const parsed = queryHelpCenterInputSchema.safeParse(toolCall.input);
            if (!parsed.success) {
                this.#chat.addToolOutput({
                    tool: queryHelpCenterToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: false, error: `Invalid input: ${formatValidationError(parsed.error)}` },
                });
                return;
            }

            try {
                const results = searchHelpCenter(parsed.data.query, { documents: helpDocuments, guides: helpGuides });
                this.#chat.addToolOutput({
                    tool: queryHelpCenterToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: true, query: parsed.data.query, results },
                });
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to search help center';
                this.#chat.addToolOutput({
                    tool: queryHelpCenterToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: false, error: message },
                });
            }
            return;
        }

        if (toolCall.toolName !== addWidgetToolName) {
            this.#chat.addToolOutput({
                tool: toolCall.toolName,
                toolCallId: toolCall.toolCallId,
                output: { error: `Unknown tool: ${toolCall.toolName}` },
            });
            return;
        }

        try {
            const requestedWidgetType =
                toolCall.input && typeof toolCall.input === 'object' && 'widgetType' in toolCall.input
                    ? toolCall.input.widgetType
                    : undefined;
            if (typeof requestedWidgetType === 'string' && !hasOwn(allWidgets, requestedWidgetType)) {
                this.#chat.addToolOutput({
                    tool: addWidgetToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: false, error: `Unknown widget type: ${requestedWidgetType}` },
                });
                return;
            }

            const parsed = addWidgetInputSchema.safeParse(toolCall.input);
            if (!parsed.success) {
                this.#chat.addToolOutput({
                    tool: addWidgetToolName,
                    toolCallId: toolCall.toolCallId,
                    output: { success: false, error: `Invalid input: ${parsed.error.message}` },
                });
                return;
            }

            const input = parsed.data;
            await this.#dispatcher.addWidget(input);
            this.#chat.addToolOutput({
                tool: addWidgetToolName,
                toolCallId: toolCall.toolCallId,
                output: createAddWidgetToolSuccessOutput(input),
            });
            this.#helpSidebarModel.close();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to add widget';
            if (TRACE) {
                this.#log.trace('addWidget tool call failed', { error: message, toolCallId: toolCall.toolCallId });
            }
            this.#chat.addToolOutput({
                tool: addWidgetToolName,
                toolCallId: toolCall.toolCallId,
                output: { success: false, error: message },
            });
        }
    }
}
